import { addMessages, init, getLocaleFromNavigator, locale, dictionary, _ } from 'svelte-i18n';
import { browser } from '$app/environment';
import { derived, get } from 'svelte/store';

// 利用可能な言語
export type LocaleCode = 'ja' | 'en' | 'zh-TW' | 'ko';
export const availableLocales: string[] = ['ja', 'en', 'zh-TW', 'ko'];

// 言語名のマッピング
export const localeNames = {
  'ja': '日本語',
  'en': 'English',
  'zh-TW': '繁體中文',
  'ko': '한국어'
};

// 言語コードと国コードのマッピング
export const localeCountries = {
  'ja': 'JP',
  'en': 'US',
  'zh-TW': 'TW',
  'ko': 'KR'
};

// すべての翻訳データを直接インポート（SSRでも確実に利用できるように）
import ja from './locales/ja.json';
import en from './locales/en.json';
import zhTW from './locales/zh-TW.json';
import ko from './locales/ko.json';

// 初期化済みフラグ
let initialized = false;

// 初期化フラグをリセットする関数
export function resetI18nInitialization() {
  initialized = false;
}

// 初期化関数
export function setupI18n() {
  // 初期化済みの場合は何もしない
  if (initialized) {
    console.log('i18n already initialized, skipping');
    return;
  }
  
  // すべての翻訳を同期的に設定（まず追加する）
  addMessages('ja', ja);
  addMessages('en', en);
  addMessages('zh-TW', zhTW);
  addMessages('ko', ko);
  
  // 初期ロケールを設定
  const initialLocale = browser ? getInitialLocale() : 'ja';
  console.log('Setting up i18n with initial locale:', initialLocale);
  
  // 初期化
  init({
    fallbackLocale: 'ja',
    initialLocale: initialLocale,
  });
  
  // 初期言語を設定
  if (browser) {
    // 確実に設定
    setTimeout(() => {
      locale.set(initialLocale);
      console.log('locale set to:', initialLocale);
    }, 0);
  }
  
  // 初期化済みフラグを設定
  initialized = true;
  console.log('i18n initialized with locale:', initialLocale);
}

// サーバーサイドレンダリング用の初期化関数
export function setupI18nSSR() {
  // 初期化済みの場合は何もしない
  if (initialized) return;
  
  // すべての翻訳を同期的に設定
  addMessages('ja', ja);
  addMessages('en', en);
  addMessages('zh-TW', zhTW);
  addMessages('ko', ko);
  
  // 初期化（サーバーサイドでは常に日本語を使用）
  init({
    fallbackLocale: 'ja',
    initialLocale: 'ja',
  });
  
  // 初期化済みフラグを設定
  initialized = true;
}

// 翻訳キーが存在するかどうかを確認する関数
export function hasTranslation(key: string, locale: string): boolean {
  let exists = false;
  
  // dictionaryからデータを取得
  const unsubscribe = dictionary.subscribe(dict => {
    exists = dict && dict[locale] && typeof dict[locale][key] !== 'undefined';
  });
  
  // 購読を解除
  unsubscribe();
  
  return exists;
}

// 初期言語を取得
function getInitialLocale(): string {
  // ローカルストレージから言語設定を取得
  const savedLocale = browser ? localStorage.getItem('preferred-locale') : null;
  
  if (savedLocale && availableLocales.includes(savedLocale)) {
    return savedLocale;
  }
  
  // ブラウザの言語設定から言語を取得
  const navigatorLocale = getLocaleFromNavigator();
  
  // 完全一致するものがあればそれを使用
  if (navigatorLocale && availableLocales.includes(navigatorLocale)) {
    return navigatorLocale;
  }
  
  // 言語コードのみで一致するものを探す
  const languageCode = navigatorLocale?.split('-')[0];
  const matchedLocale = availableLocales.find(locale => locale.startsWith(languageCode || ''));
  
  return matchedLocale || 'ja'; // デフォルトは日本語
}

// 言語を変更する関数
export function changeLocale(newLocale: string) {
  if (!availableLocales.includes(newLocale)) {
    console.error(`Locale ${newLocale} is not available`);
    return;
  }
  
  console.log(`Changing locale to ${newLocale}`);
  
  // 翻訳ファイルがまだロードされていなければロード
  if (browser) {
    // 初期化フラグをリセット
    resetI18nInitialization();
    
    // すべての翻訳を再ロード（確実に追加）
    addMessages('ja', ja);
    addMessages('en', en);
    addMessages('zh-TW', zhTW);
    addMessages('ko', ko);
    
    // 強制的にロケール設定
    locale.set(newLocale);
    
    // ローカルストレージに保存
    localStorage.setItem('preferred-locale', newLocale);
    
    console.log(`Locale changed to ${newLocale}, reloading page in 100ms`);
    
    // ページをリロードして言語変更を反映
    if (typeof window !== 'undefined') {
      // 少し遅延を入れて、言語設定が保存されるのを待つ
      setTimeout(() => {
        window.location.reload();
      }, 100);
    }
  } else {
    locale.set(newLocale);
    if (browser) {
      localStorage.setItem('preferred-locale', newLocale);
    }
  }
}

// 翻訳ユーティリティ関数
export function t(key: string, defaultValue: string, values?: Record<string, any>): string {
  // 現在の言語を取得
  const currentLocale = get(locale) || 'ja';
  
  try {
    // 翻訳関数から値を取得
    const translationFn = get(_);
    if (translationFn) {
      const translated = translationFn(key, values);
      // 翻訳が見つかった場合はそれを使用
      if (translated && translated !== key) {
        return translated;
      }
    }
    
    // 翻訳が見つからない場合はデフォルト値を使用
    return defaultValue;
  } catch (error) {
    console.error(`Translation error for key ${key}:`, error);
    return defaultValue;
  }
}

// 現在の言語を取得するためのストア
export const currentLocale = derived(locale, $locale => $locale);

// 現在の言語の国コードを取得するためのストア
export const currentCountry = derived(locale, $locale => {
  return $locale && localeCountries[$locale as keyof typeof localeCountries] || 'JP';
});